import React, { PropsWithChildren } from "react";
import { Image } from "react-bootstrap";

import "./ProfilePage.scss";
import HeaderWithTitleAndBackButton from "../../components/Headers/HeaderWithTitleAndBackButton/HeaderWithTitleAndBackButton";
import useAuth from "../../hooks/useAuth";
import DownloadAppPrompt from "../../components/DownloadAppPrompt/DownloadAppPrompt";
import useDisclosure from "../../hooks/useDisclosure";
import { Link } from "react-router-dom";
import ArrowRightIcon from "../../components/icons/ArrowRightIcon";
import ImageTelegram from "../../images/telegram-brand.svg";
import ImageTikTok from "../../images/tiktok-rounded.png";
import ImageX from "../../images/x-rounded.svg";
import ImageUserIcon from "../../images/user-filled.png";
import { LINKS, usePlayGameMetaDescription } from "../../utils/constants";
import FilledButton, {
  ButtonSizes,
} from "../../components/dashboard-page/FilledButton/FilledButton";
import SignOutModal from "../../components/modals/SignOutModal/SignOutModal";
import useMyTopGamesQuery from "../../react-query/queries/use-my-top-games";
import { ROUTES } from "../../utils/constants";
import { getTimeDifferenceToNow } from "../../utils/helpers";
import ImageIcon from "../../components/icons/ImageIcon";
import {
  extractOgTitleFromMainTitle,
  renderHeaders,
} from "../../utils/renderHeaders";
import useGetSocialLinksByCountryQuery from "../../react-query/queries/use-get-social-links-by-country-query";
import Skeleton from "../../components/Skeleton/Skeleton";
import { useTranslation } from "react-i18next";

function LoadingSkeleton() {
  return (
    <div className="d-flex flex-column px-3 py-3 gap-2">
      <Skeleton height={16} width={60} />
      <Skeleton height={164} width="100%" />
    </div>
  );
}

function ProfilePage() {
  const { userProfile } = useAuth();
  const { t } = useTranslation();
  const metaDescriptions = usePlayGameMetaDescription();
  const {
    isOpen: isDownloadAppPromptOpen,
    onClose: onCloseDownloadAppPrompt,
    onOpen: onOpenDownloadAppPrompt,
  } = useDisclosure();
  const {
    isOpen: isSignOutModalOpen,
    onClose: onCloseSignOutModal,
    onOpen: onOpenSignOutModal,
  } = useDisclosure();
  const { data: myTopGames, isLoading: isTopGamesLoading } =
    useMyTopGamesQuery();

  const { socialLinks } = useGetSocialLinksByCountryQuery(
    userProfile?.countryCode
  );

  return (
    <>
      {renderHeaders({
        title: t("metadata:profile_hyper_gaming_app"),
        description: metaDescriptions.generic,
        ogTitle: extractOgTitleFromMainTitle(
          t("metadata:profile_hyper_gaming_app")
        ),
      })}

      <div className="profile-page">
        <HeaderWithTitleAndBackButton title={{ text: "Profile" }} />
        <section className="hero">
          <div className="rounded-avatar">
            <Image src={userProfile?.avatar?.url || ImageUserIcon} alt="" />
          </div>
          <p className="username">@{userProfile?.username}</p>
          <button className="edit" onClick={onOpenDownloadAppPrompt}>
            Edit profile
          </button>
        </section>
        {isTopGamesLoading ? (
          <LoadingSkeleton />
        ) : (
          !!myTopGames?.records?.length && (
            <section>
              <h2 className="section-heading">Your top games</h2>
              <div className="card">
                {myTopGames?.records?.map(
                  ({ game: { name, slug, id, thumbnail }, lastPlayedAt }) => {
                    const lastPlayedInfo = getTimeDifferenceToNow(lastPlayedAt);
                    const lastPlayed = `${Math.abs(
                      lastPlayedInfo?.difference || 0
                    )} ${lastPlayedInfo?.unit}${
                      lastPlayedInfo?.difference === 1 ? "" : "s"
                    } ago`;
                    return (
                      <GameItem
                        key={id}
                        href={`${ROUTES.GAMES.ROOT}/${slug}/play`}
                        thumbnail={thumbnail}
                        gameName={name}
                        lastPlayed={lastPlayed}
                      />
                    );
                  }
                )}
              </div>
            </section>
          )
        )}
        <section>
          <h2 className="section-heading">Community</h2>
          <div className="card">
            <CommunityItem logo={ImageX} href={socialLinks?.xUrl}>
              Join our X community
            </CommunityItem>
            <CommunityItem logo={ImageTelegram} href={socialLinks?.telegramUrl}>
              Join our Telegram community
            </CommunityItem>
            <CommunityItem logo={ImageTikTok} href={socialLinks?.tikTokUrl}>
              Follow us on TikTok
            </CommunityItem>
          </div>
        </section>
        <FilledButton
          size={ButtonSizes.MD}
          style={{ color: "#EF233C", background: "#F5F5F5" }}
          onClick={onOpenSignOutModal}
        >
          Sign Out
        </FilledButton>
        <footer>
          <a
            href={LINKS.WEBPAGE.HYPER_LANDING_PAGE}
            target="_blank"
            rel="noopener noreferrer"
          >
            About Hyper
          </a>
          <span className="dot" />
          <a
            href={LINKS.WEBPAGE.PRIVACY_POLICY}
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
          <span className="dot" />
          <a
            href={LINKS.WEBPAGE.TERMS_AND_CONDITIONS}
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms & Conditions
          </a>
        </footer>

        <DownloadAppPrompt
          isOpen={isDownloadAppPromptOpen}
          onOpen={onOpenDownloadAppPrompt}
          onClose={onCloseDownloadAppPrompt}
        />
        <SignOutModal show={isSignOutModalOpen} onHide={onCloseSignOutModal} />
      </div>
    </>
  );
}
export default ProfilePage;

interface GameItemProps {
  thumbnail?: string;
  gameName?: string;
  lastPlayed?: string;
  href: string;
}

function GameItem({ thumbnail, gameName, lastPlayed, href }: GameItemProps) {
  return (
    <Link to={href} className="game-item w-100">
      <div className="d-flex align-items-center justify-content-between w-100">
        <div className="img-text-wrapper d-flex align-items-center">
          {thumbnail ? (
            <Image src={thumbnail} alt="" height={40} width={40} />
          ) : (
            <ImageIcon height={40} width={40} fill="#6c53e8" />
          )}
          <div>
            <p className="game-name">{gameName}</p>
            <p className="last-played">
              Last played: <span>{lastPlayed}</span>
            </p>
          </div>
        </div>
        <ArrowRightIcon fill="#939393" />
      </div>
    </Link>
  );
}

interface CommunityItemProps {
  logo?: string;
  href?: string | null;
}

function CommunityItem({
  logo,
  href,
  children,
}: PropsWithChildren<CommunityItemProps>) {
  return (
    <a
      href={href || "#!"}
      target="_blank"
      rel="noopener noreferrer"
      className="community-item w-100"
    >
      <div className="d-flex align-items-center justify-content-between w-100">
        <div className="img-text-wrapper d-flex align-items-center">
          <Image src={logo} alt="" height={32} width={32} />
          <div className="cta">{children}</div>
        </div>
        <ArrowRightIcon fill="#939393" />
      </div>
    </a>
  );
}
